import { getSDK } from '@/store/sdkStore';

// ============================================================================
// lib/polls.ts — вспомогательное для страницы типов опросов (§19):
// типы SSoT-сервера, порядок/подписи колонок протокола, детерминированный
// резолв типа по идентификатору и признак psy-методики (для выбора клиента).
// Вынесено из FilledPollsPage (SRP, файл страницы ≤ 400 строк).
// ============================================================================

export interface PollTypeInfo {
    code: string;
    name: string;
    synonym?: string;
    status?: string;
    scoring?: string;
    templateId?: string;
}

export interface PollQuestionInfo {
    id?: string;
    ord?: number;
    text?: string;
    type?: string;
    options?: string[];
    required?: boolean;
}

export interface ProtocolColumn { name: string; label: string; dataType: string }
export type ProtocolRow = Record<string, unknown>;

// Порядок «стандартных» колонок протокола (дата/субъект/итог — как в 1С).
export const LEADING_COLS = ['created_at', 'entity_id', 'subject_id', 'score', 'passed', 'conclusion', 'updated_at'];
// Служебные колонки протокола — в таблице НЕ показываются.
export const TECH_COLS = new Set(['owner_user_id', 'scores', 'version', 'modified_at', 'session_id']);
export const COL_LABELS: Record<string, string> = {
    created_at: 'Дата', updated_at: 'Изменено', created_by: 'Создал', updated_by: 'Изменил',
    entity_id: 'Пользователь', subject_id: 'Объект тестирования',
    score: 'Балл', passed: 'Пройдено', conclusion: 'Заключение',
};

// psy-методика: у типа опроса есть реквизит «Сессия» (session_id) — поставка psy
// создаёт его КАЖДОМУ своему типу; обычные опросы такого реквизита не имеют.
export const PSY_SESSION_FIELD = 'session_id';
export const PSY_CLIENTS_CATALOG = 'psy_clients';

// Справочник psy_clients в базе — кэш на сессию страницы (признак вертикали psy).
let psyClientsCatalog: boolean | null = null;
export async function hasPsyClientsCatalog(): Promise<boolean> {
    if (psyClientsCatalog !== null) return psyClientsCatalog;
    const r: any = await getSDK().metadata.listCatalogs({});
    psyClientsCatalog = ((r?.items || []) as any[]).some((c: any) => String(c.code || '') === PSY_CLIENTS_CATALOG);
    return psyClientsCatalog;
}

// resolvePollType — детерминированный резолв типа опроса по входящему
// идентификатору: СНАЧАЛА код (SSoT сервера), затем синоним, затем имя.
// Внешняя навигация (раздел/виджет/легаси-данные) исторически могла передать
// СИНОНИМ методики — тип всё равно находится, и тест открывается по КОДУ,
// а не проваливается в пустой список «Нет данных».
export function resolvePollType(types: PollTypeInfo[], id: string): PollTypeInfo | undefined {
    const key = id.trim().toLowerCase();
    if (!key) return undefined;
    return types.find((t) => String(t.code || '').trim().toLowerCase() === key)
        || types.find((t) => String(t.synonym || '').trim().toLowerCase() === key)
        || types.find((t) => String(t.name || '').trim().toLowerCase() === key);
}

// findMethodologyReportId — отчёт по методике: поставка psy кладёт отчёты в
// группу «Отчёты» с ИМЕНЕМ, равным синониму методики (запасной ключ — имя типа).
// Отчёта нет — пусто: кнопка «Отчёт» НЕ показывается (заглушек нет).
export async function findMethodologyReportId(type: PollTypeInfo | undefined): Promise<string> {
    const keys = [String(type?.synonym || '').trim(), String(type?.name || '').trim()].filter((k) => k !== '');
    if (keys.length === 0) return '';
    const r: any = await getSDK().report.listReports({ folderId: '' });
    const items = ((r?.items || []) as any[]);
    for (const key of keys) {
        const found = items.find((it: any) => String(it.name || '').trim() === key);
        if (found) return String(found.id || '');
    }
    return '';
}
